import { makeAutoObservable } from 'mobx'
import type { PermissionType } from '../util/permissions'
import Radom, { IOrganization, User } from './Radom'

const IMPERSONATION_ALLOWED_ORG_IDS = new Set([
  '00000000-0000-0000-0000-000000000000',
  '00000000-0000-0000-0000-000000000999'
])
const IMPERSONATION_ORG_ID_LOCAL_STORAGE_KEY = 'IMPERSONATION_ORG_ID'

class UserState {
  private _defaultOrganizationId?: string

  isLoading = false
  // Wallet sign-in is gone; these stay so the wallet-dependent widgets behave
  // as they always did for email and SSO users: no connected account.
  isConnected = false
  accounts: string[] = []
  _user?: User = undefined
  userFetch?: Promise<User>

  organization?: IOrganization = undefined
  organizationFetch?: Promise<IOrganization>

  constructor() {
    makeAutoObservable(this)
  }

  get selectedOrganizationId(): string | undefined {
    return this._user?.defaultOrganizationId
  }

  get connectedAccount(): Promise<string> {
    return Promise.resolve(this.accounts[0])
  }

  get permissionSet(): Set<string> {
    return new Set(this._user?.permissions ?? [])
  }

  get permissionsLoaded(): boolean {
    return this._user?.permissions !== undefined
  }

  hasPermission = (
    perms?: PermissionType | PermissionType[],
    opts?: { requireAll?: boolean }
  ): boolean => {
    const required = perms === undefined ? [] : Array.isArray(perms) ? perms : [perms]
    if (required.length === 0) {
      return true
    }
    if (!this.permissionsLoaded) {
      return false
    }
    const set = this.permissionSet
    return opts?.requireAll === false
      ? required.some(p => set.has(p))
      : required.every(p => set.has(p))
  }

  getUser = async (): Promise<User> => {
    if (!this._user) {
      if (!this.userFetch) {
        this.userFetch = Radom.getUser()
      }
      this._user = await this.userFetch
      this._defaultOrganizationId = this._user.defaultOrganizationId
      const tokenStr = atob(Radom.token)
      const token = JSON.parse(tokenStr)
      if (!token.otp_verified && this._user && this._user.otpVerified && this._user.otpEnabled) {
        Radom.isLoggedIn = false
        Radom.requires2fa = true
      }
      this.userFetch = undefined
    }
    const isQaSeed =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('qa') === '1'
    const impersonationOrg = localStorage.getItem(IMPERSONATION_ORG_ID_LOCAL_STORAGE_KEY)
    const canUseImpersonation = !!this._defaultOrganizationId &&
      IMPERSONATION_ALLOWED_ORG_IDS.has(this._defaultOrganizationId)

    if (impersonationOrg && !isQaSeed && canUseImpersonation) {
      this._user.defaultOrganizationId = impersonationOrg
    } else if (impersonationOrg && !isQaSeed) {
      localStorage.removeItem(IMPERSONATION_ORG_ID_LOCAL_STORAGE_KEY)
    }

    return this._user
  }

  getOrganization = async(forceFetch = false): Promise<IOrganization> => {
    const user = await this.getUser()
    const selectedOrganizationId = user?.defaultOrganizationId

    if (!this.organization || forceFetch || this.organization.organizationId !== selectedOrganizationId) {
      if (!this.organizationFetch) {
        this.organizationFetch = Radom.getOrganizationById(selectedOrganizationId)
      }
      try {
        this.organization = await this.organizationFetch
      } finally {
        this.organizationFetch = undefined
      }
    }

    return this.organization
  }

}

export default new UserState()
